import React, { useEffect, useState } from 'react';
import Card, { CardHeader, CardContent } from '../../../components/ui/Card';
import { ChartBarIcon, GlobeAltIcon, UserGroupIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

interface CohortSummary {
  totalCohorts: number;
  averageRetention: number;
  cohorts: { cohort: string; month: string; size: number; retained: number; retentionRate: number }[];
}

interface GeographicSummary {
  totalCountries: number;
  topCountries: { country: string; patrons: number; percentage: number }[];
  regionBreakdown: { region: string; percentage: number }[];
}

interface EngagementSummary {
  period: { start: string; end: string };
  totals: { posts: number; comments: number; likes: number; views: number };
  optimalPosting: { day: string; hour: number; score: number }[];
}

interface ForecastSummary {
  totalProjectedRevenue: number;
  months: { month: string; projectedRevenue: number; confidence: number }[];
}

interface LoadingState { cohorts: boolean; geo: boolean; engagement: boolean; forecast: boolean; }

const PatronInsightsPage: React.FC = () => {
  const [cohort, setCohort] = useState<CohortSummary | null>(null);
  const [geo, setGeo] = useState<GeographicSummary | null>(null);
  const [engagement, setEngagement] = useState<EngagementSummary | null>(null);
  const [forecast, setForecast] = useState<ForecastSummary | null>(null);
  const [loading, setLoading] = useState<LoadingState>({ cohorts: true, geo: true, engagement: true, forecast: true });
  const [error, setError] = useState<string | null>(null);
  const [periodDays, setPeriodDays] = useState(30);

  const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;

  useEffect(() => {
    loadData();
  }, [periodDays]);

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const loadData = async () => {
    setError(null);
    loadCohorts();
    loadGeo();
    loadEngagement();
    loadForecast();
  };

  const loadCohorts = async () => {
    setLoading(l => ({ ...l, cohorts: true }));
    try {
      const res = await axios.get(`${API_BASE_URL}/api/analytics/cohort/summary`, { headers: authHeaders });
      setCohort(res.data.summary || res.data);
    } catch (e:any) {
      console.error('Cohort load failed', e);
      setError('Failed to load cohort data');
    } finally { setLoading(l => ({ ...l, cohorts: false })); }
  };

  const loadGeo = async () => {
    setLoading(l => ({ ...l, geo: true }));
    try {
      const res = await axios.get(`${API_BASE_URL}/api/analytics/geographic/summary`, { headers: authHeaders });
      setGeo(res.data.summary || res.data);
    } catch (e:any) {
      console.error('Geo load failed', e);
      setError('Failed to load geographic data');
    } finally { setLoading(l => ({ ...l, geo: false })); }
  };

  const loadEngagement = async () => {
    setLoading(l => ({ ...l, engagement: true }));
    try {
      const res = await axios.get(`${API_BASE_URL}/api/analytics/engagement/summary`, { headers: authHeaders, params: { periodDays } });
      setEngagement(res.data.summary || res.data);
    } catch (e:any) {
      console.error('Engagement load failed', e);
      setError('Failed to load engagement data');
    } finally { setLoading(l => ({ ...l, engagement: false })); }
  };

  const loadForecast = async () => {
    setLoading(l => ({ ...l, forecast: true }));
    try {
      const res = await axios.get(`${API_BASE_URL}/api/analytics/revenue-forecast/summary`, { headers: authHeaders });
      setForecast(res.data.summary || res.data);
    } catch (e:any) {
      console.error('Forecast load failed', e);
      setError('Failed to load forecast data');
    } finally { setLoading(l => ({ ...l, forecast: false })); }
  };

  const overallLoading = Object.values(loading).some(v => v);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Patron Insights</h1>
          <p className="text-gray-600 mt-1">Retention, geography, engagement & forecast overview</p>
        </div>
        <select
          value={periodDays}
          onChange={e => setPeriodDays(parseInt(e.target.value))}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
        >
          <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
            <option value={365}>Last year</option>
        </select>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
      )}

      {overallLoading && (
        <div className="text-center py-12 text-gray-500">Loading insights...</div>
      )}

      {!overallLoading && (
        <>
          {/* Key Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-indigo-100 rounded-lg"><UserGroupIcon className="w-6 h-6 text-indigo-600" /></div>
                  <span className="text-sm text-gray-500">Cohorts</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">{cohort?.totalCohorts || 0}</h3>
                <p className="text-sm text-gray-600 mt-1">Avg retention {(cohort?.averageRetention || 0).toFixed(1)}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-emerald-100 rounded-lg"><ChartBarIcon className="w-6 h-6 text-emerald-600" /></div>
                  <span className="text-sm text-gray-500">Engagement</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">{engagement?.totals.views?.toLocaleString() || 0}</h3>
                <p className="text-sm text-gray-600 mt-1">{engagement?.totals.likes || 0} likes, {engagement?.totals.comments || 0} comments</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-blue-100 rounded-lg"><GlobeAltIcon className="w-6 h-6 text-blue-600" /></div>
                  <span className="text-sm text-gray-500">Countries</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">{geo?.totalCountries || 0}</h3>
                <p className="text-sm text-gray-600 mt-1">Top: {geo?.topCountries?.[0]?.country || '—'}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-orange-100 rounded-lg"><ArrowTrendingUpIcon className="w-6 h-6 text-orange-600" /></div>
                  <span className="text-sm text-gray-500">Forecast</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">${(forecast?.totalProjectedRevenue || 0).toLocaleString()}</h3>
                <p className="text-sm text-gray-600 mt-1">Next month: ${forecast?.months?.[0]?.projectedRevenue?.toLocaleString() || 0}</p>
              </CardContent>
            </Card>
          </div>

          {/* Cohort & Geographic */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-gray-900">Recent Cohorts</h3>
              </CardHeader>
              <CardContent>
                {cohort?.cohorts?.length ? (
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="py-2 pr-4">Cohort</th>
                          <th className="py-2 pr-4">Month</th>
                          <th className="py-2 pr-4">Size</th>
                          <th className="py-2 pr-4">Retained</th>
                          <th className="py-2 pr-4">Retention</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cohort.cohorts.slice(0,10).map(c => (
                          <tr key={c.cohort+c.month} className="border-b last:border-0">
                            <td className="py-2 pr-4 font-medium text-gray-900">{c.cohort}</td>
                            <td className="py-2 pr-4">{c.month}</td>
                            <td className="py-2 pr-4">{c.size}</td>
                            <td className="py-2 pr-4">{c.retained}</td>
                            <td className="py-2 pr-4">{c.retentionRate.toFixed(1)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">No cohort data available.</div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-gray-900">Top Countries</h3>
              </CardHeader>
              <CardContent>
                {geo?.topCountries?.length ? (
                  <ul className="divide-y">
                    {geo.topCountries.slice(0,8).map(c => (
                      <li key={c.country} className="py-3 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{c.country}</p>
                          <p className="text-xs text-gray-500">{c.patrons} patrons</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-40 bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div className="h-2 bg-gradient-to-r from-blue-500 to-indigo-500" style={{ width: `${c.percentage}%` }} />
                          </div>
                          <span className="text-sm font-medium text-gray-700">{c.percentage.toFixed(1)}%</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-gray-500 text-sm">No geographic data.</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Engagement & Optimal Times */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-gray-900">Engagement Totals</h3>
              </CardHeader>
              <CardContent>
                {engagement ? (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Posts</p>
                      <p className="font-semibold text-gray-900">{engagement.totals.posts}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Views</p>
                      <p className="font-semibold text-gray-900">{engagement.totals.views}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Likes</p>
                      <p className="font-semibold text-gray-900">{engagement.totals.likes}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Comments</p>
                      <p className="font-semibold text-gray-900">{engagement.totals.comments}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">No engagement data.</div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-gray-900">Optimal Posting Times</h3>
              </CardHeader>
              <CardContent>
                {engagement?.optimalPosting?.length ? (
                  <ol className="space-y-2 text-sm list-decimal list-inside">
                    {engagement.optimalPosting.slice(0,6).map(t => (
                      <li key={t.day + t.hour} className="flex items-center justify-between">
                        <span className="text-gray-700">{t.day} @ {t.hour}:00</span>
                        <span className="px-2 py-1 text-xs rounded bg-indigo-50 text-indigo-700 font-medium">Score {t.score}</span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <div className="text-gray-500 text-sm">No optimal timing data.</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Forecast */}
          <div className="mt-6">
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-gray-900">Revenue Forecast (Next Period)</h3>
              </CardHeader>
              <CardContent>
                {forecast?.months?.length ? (
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="py-2 pr-4">Month</th>
                          <th className="py-2 pr-4">Projected Revenue</th>
                          <th className="py-2 pr-4">Confidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {forecast.months.slice(0,6).map(m => (
                          <tr key={m.month} className="border-b last:border-0">
                            <td className="py-2 pr-4 font-medium text-gray-900">{m.month}</td>
                            <td className="py-2 pr-4">${m.projectedRevenue.toLocaleString()}</td>
                            <td className="py-2 pr-4">
                              <div className="flex items-center gap-2">
                                <div className="w-32 bg-gray-200 rounded-full h-2 overflow-hidden">
                                  <div className="h-2 bg-green-500" style={{ width: `${(m.confidence*100).toFixed(0)}%` }} />
                                </div>
                                <span>{(m.confidence * 100).toFixed(0)}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">No forecast data.</div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default PatronInsightsPage;
